/* ─────────────────────────────────────────────────────────────
   DependentSelectField — A cascading select whose options depend
   on another field's value (read from enrichedContext via parentField).

   Config via field.props:
   - parentField (string)       — form field name whose value drives options
   - optionsByParentKey (string) — runtimeContext key holding Record<parentValue, string[]>
   - allowOther (boolean)       — enable "Other…" free-text escape

   The field stores the resolved final value (role string), not a sentinel.
   In "Other…" mode, the text input directly drives onChange.
   ───────────────────────────────────────────────────────────── */

import { useState, useRef, useEffect } from 'react';
import type { FieldRendererProps, FieldDisplayProps } from '../../config/types';
import { Combobox } from '@/components/ui/Combobox';
import { Input } from '@/components/ui/Input';
import { Text } from '@/components/ui/Text';

const OTHER_SENTINEL = '__OTHER__';

export function DependentSelectField({
  field,
  value,
  onChange,
  onBlur,
  error,
  runtimeContext,
}: FieldRendererProps) {
  const parentField = (field.props?.parentField as string) ?? '';
  const optionsByParentKey = (field.props?.optionsByParentKey as string) ?? '';
  const allowOther = (field.props?.allowOther as boolean) ?? false;

  // enrichedContext (from FormBuilder) merges live form values into runtimeContext,
  // so reading runtimeContext[parentField] gives the live sibling field value.
  const parentValue = (runtimeContext[parentField] as string) ?? '';
  const optionsByParent = (runtimeContext[optionsByParentKey] as Record<string, string[]>) ?? {};

  const rawOptions = parentValue ? (optionsByParent[parentValue] ?? []) : [];
  const options: { value: string; label: string }[] = rawOptions.map((r) => ({ value: r, label: r }));

  if (allowOther) {
    options.push({ value: OTHER_SENTINEL, label: 'Other…' });
  }

  const currentValue = (value as string) ?? '';
  const isInOptions = rawOptions.includes(currentValue);

  // "Other" mode: existing value that's not a taxonomy option
  const isOtherMode = currentValue !== '' && !isInOptions;

  // Pending: user selected "Other…" but hasn't typed yet (value is empty)
  const [isOtherPending, setIsOtherPending] = useState(false);

  const showOtherInput = isOtherMode || isOtherPending;
  const selectDisplayValue = showOtherInput ? OTHER_SENTINEL : currentValue;

  // When parent changes: reset pending state and clear value if no longer valid
  const prevParentRef = useRef(parentValue);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  useEffect(() => {
    if (prevParentRef.current === parentValue) return;
    prevParentRef.current = parentValue;
     
    setIsOtherPending(false);
    const newOptions = parentValue ? (optionsByParent[parentValue] ?? []) : [];
    const cv = (value as string) ?? '';
    if (cv && !newOptions.includes(cv)) {
      onChangeRef.current('');
    }
  // Only react to parent changes; value/onChange read via refs/direct closure
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentValue]);

  function handleSelectChange(v: string) {
    if (v === OTHER_SENTINEL) {
      setIsOtherPending(true);
      onChange('');
    } else {
      setIsOtherPending(false);
      onChange(v);
    }
    onBlur();
  }

  const hasParent = !!parentValue;
  const isDisabled = field.isDisabled || !hasParent;

  return (
    <div>
      <Combobox
        id={field.id}
        options={options}
        value={selectDisplayValue}
        onValueChange={handleSelectChange}
        placeholder={hasParent ? (field.placeholder ?? 'Select…') : 'Select type first…'}
        disabled={isDisabled}
        className={`bg-surface-elevated${error ? ' border-danger' : ''}`}
      />
      {showOtherInput && (
        <Input
          value={isOtherMode ? currentValue : ''}
          onChange={(e) => {
            setIsOtherPending(false);
            onChange(e.target.value);
          }}
          onBlur={onBlur}
          placeholder="Enter role title"
          className="mt-1.5"
          aria-label="Custom role"
        />
      )}
    </div>
  );
}

export function DependentSelectDisplay({ field: _field, value }: FieldDisplayProps) {
  const displayValue = (value as string) ?? '';
  if (!displayValue) return <Text variant="body-md" color="muted">—</Text>;
  return <Text variant="body-md">{displayValue}</Text>;
}
